import EyeHide from '@lmt/unicorn-shared/app/icons/eye-hide.svg?react';
import EyeShow from '@lmt/unicorn-shared/app/icons/eye-show.svg?react';
import clsx from 'clsx';
import React, { KeyboardEventHandler, Ref, useMemo, useState } from 'react';

import styles from './Input.module.scss';

export type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  containerRef?: Ref<HTMLDivElement>;
  onEnterPressed?: (text: string) => void;
  wrapperClassName?: string;
  errorText?: string;
  helperText?: string;
  type?: string;
  badge?: React.ReactNode;
  /*
   * endAdornment is an element that is placed on the right of the Input (visually inside the input)
   */
  endAdornment?: React.ReactNode;
  endAdornmentClassname?: string;
  errorClassname?: string;
  endAdornmentVisibleOnHover?: boolean;
  testId?: string;
  /*
   * 'normal' - input will have minimal width
   * 'formInput' - input will have the standard width (as all form elements)
   */
  variant?: 'normal' | 'formInput';

  /*
   * This prop might be set if input is part of Autocomplete (DropDown/Combobox/etc).
   * If we don't do this, `selectedValue` ends up as DOM attribute which isn't what we want.
   * It might be better implemented by wrapping inputs that are used in Autocomplete, but it's less of a hassle like it is now.
   */
  selectedValue?: unknown;
};

const decimalSeparator = Intl.NumberFormat().format(1.1)[1];

export const Input = React.forwardRef<HTMLInputElement, Props>(
  function Input(props, ref) {
    const {
      containerRef,
      onClick,
      style,
      className,
      helperText,
      errorText,
      onEnterPressed,
      type,
      badge,
      endAdornment: endAdornmentProps,
      endAdornmentClassname,
      endAdornmentVisibleOnHover,
      variant,
      testId,
      selectedValue,
      ...rest
    } = props;
    const isError = !!errorText;
    const onKeyDownDefault: KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (type === 'number' && e.key === '.' && decimalSeparator !== '.') {
        e.preventDefault();
      }
    };

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
      onKeyDownDefault(e);
      if (e.key === 'Enter') {
        if (onEnterPressed) {
          onEnterPressed(e.currentTarget.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    const [showPassword, setShowPassword] = useState<boolean | undefined>(
      type === 'password' ? false : undefined,
    );

    const endAdornment = useMemo(() => {
      if (type !== 'password') {
        if (!props.endAdornment) return undefined;
        return { element: props.endAdornment, onClick: props.onClick };
      }

      const eyeIcon = (
        <span
          onClick={() => setShowPassword((v) => !v)}
          className={styles.passwordEye}
        >
          {showPassword ? (
            <EyeHide className={styles.eyeIcon} />
          ) : (
            <EyeShow className={styles.eyeIcon} />
          )}
        </span>
      );

      if (props.endAdornment) {
        return {
          element: (
            <span className={clsx(styles.endAdornment, styles.twoAdornments)}>
              {eyeIcon}
              <span onClick={props.onClick}>{props.endAdornment}</span>
            </span>
          ),
        };
      }

      return { element: eyeIcon };
    }, [type, showPassword, props.endAdornment, props.onClick]);

    return (
      <div onClick={onClick} className={styles.inputContainer}>
        <div ref={containerRef} className={styles.inputWithAdornmentWrapper}>
          <input
            ref={ref}
            className={clsx(
              styles.input,
              endAdornment ? styles.inputWithAdornment : null,
              variant === 'normal' ? styles.nonFormInput : null,
              className,
            )}
            style={style}
            data-error={isError.toString()}
            onKeyDown={onEnterPressed ? onKeyDown : onKeyDownDefault}
            type={showPassword ? 'input' : type}
            data-test-id={testId}
            {...rest}
            autoComplete={props.autoComplete ?? 'off'}
          />
          {endAdornment ? (
            <div
              className={clsx(
                styles.endAdornment,
                type === 'password' && styles.passwordEye,
                endAdornmentClassname,
              )}
              onClick={endAdornment.onClick}
              onFocus={props.onFocus}
              onMouseDown={props.disabled ? undefined : props.onMouseDown} // fix #41409: disabled dropdown is still opened by clicking on arrow
              data-hover={props.endAdornmentVisibleOnHover}
            >
              {endAdornment.element}
            </div>
          ) : null}
          {badge}
        </div>
        {!!(helperText || errorText) && (
          <div
            data-error={isError.toString()}
            className={clsx(styles.helperText, props.errorClassname)}
          >
            {helperText || errorText}
          </div>
        )}
      </div>
    );
  },
);
